import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, Stack,
  InputAdornment, IconButton, CircularProgress, LinearProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { authAPI } from '../services/api';
import { AuthShell, AuthBrand, AuthCard } from './Login';

const PRIMARY   = '#1a1a2ed4';
const SURFACE   = '#FFFFFF';
const TEXT_DARK = '#1A1A2E';
const TEXT_MID  = '#6B7280';
const TEXT_LIGHT= '#B0B7C3';
const DIVIDER   = '#EEEEF5';
const BG        = '#F4F4F8';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: BG,
    fontSize: '0.875rem',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: DIVIDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY, borderWidth: '2px' },
  },
};

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: 'inherit' };
  let score = 0;
  if (pw.length >= 8)            score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: '',       color: 'inherit' },
    { label: 'Weak',   color: 'error'   },
    { label: 'Fair',   color: 'warning' },
    { label: 'Good',   color: 'info'    },
    { label: 'Strong', color: 'success' },
  ];
  return { score, ...map[score] };
}

export default function ResetPassword() {
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const token     = params.get('token') || '';
  const email     = params.get('email') || '';

  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [done, setDone]               = useState(false);

  const strength = passwordStrength(password);
  const mismatch = confirm.length > 0 && password !== confirm;

  useEffect(() => {
    if (!token || !email) setError('Invalid or expired reset link. Please request a new one.');
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, email, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthBrand />

      {done ? (
        <Box
          sx={{
            bgcolor: SURFACE, border: `1px solid ${DIVIDER}`,
            borderRadius: 3, p: 3, textAlign: 'center',
            boxShadow: '0 4px 16px rgba(108,99,255,0.06)',
          }}
        >
          <Box
            sx={{
              width: 52, height: 52, borderRadius: '50%',
              bgcolor: '#4CAF82',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2,
              boxShadow: '0 4px 12px rgba(76,175,130,0.3)',
            }}
          >
            <CheckCircle sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Typography fontWeight={700} sx={{ mb: 1, color: TEXT_DARK, fontSize: '1rem' }}>
            Password updated
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: TEXT_MID, fontSize: '0.8125rem', lineHeight: 1.6 }}>
            Your password has been reset. You can now sign in with your new credentials.
          </Typography>
          <Button
            fullWidth variant="contained"
            onClick={() => navigate('/login')}
            sx={{
              py: 1.25, fontWeight: 700, borderRadius: '12px',
              bgcolor: PRIMARY,
              boxShadow: '0 4px 16px rgba(108,99,255,0.35)',
              '&:hover': { bgcolor: '#5850E6', boxShadow: '0 6px 20px rgba(108,99,255,0.45)' },
            }}
          >
            Sign in
          </Button>
        </Box>
      ) : (
        <AuthCard
          title="Set new password"
          subtitle={email ? `Choosing a new password for ${email}.` : undefined}
        >
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5, py: 0.75, borderRadius: 2, fontSize: '0.8125rem',
                bgcolor: 'rgba(255,82,82,0.06)', border: '1px solid rgba(255,82,82,0.2)',
                color: '#FF5252', '& .MuiAlert-icon': { color: '#FF5252', fontSize: 16 },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {/* New password */}
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, fontSize: '0.8125rem', color: TEXT_DARK }}>
                  New password
                </Typography>
                <TextField
                  required fullWidth autoFocus
                  type={showPass ? 'text' : 'password'} size="small"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small" edge="end" tabIndex={-1}
                          onClick={() => setShowPass(v => !v)}
                          sx={{ color: TEXT_LIGHT, '&:hover': { color: TEXT_MID } }}
                        >
                          {showPass ? <VisibilityOff sx={{ fontSize: 17 }} /> : <Visibility sx={{ fontSize: 17 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {password.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(strength.score / 4) * 100}
                      color={strength.color}
                      sx={{ height: 3, borderRadius: 2, bgcolor: DIVIDER }}
                    />
                    <Typography variant="caption" color={`${strength.color}.main`} sx={{ fontSize: '0.7rem', mt: 0.25, display: 'block' }}>
                      {strength.label}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Confirm password */}
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, fontSize: '0.8125rem', color: TEXT_DARK }}>
                  Confirm password
                </Typography>
                <TextField
                  required fullWidth
                  type={showConfirm ? 'text' : 'password'} size="small"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat your new password"
                  error={mismatch}
                  helperText={mismatch ? 'Passwords do not match' : ''}
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small" edge="end" tabIndex={-1}
                          onClick={() => setShowConfirm(v => !v)}
                          sx={{ color: TEXT_LIGHT, '&:hover': { color: TEXT_MID } }}
                        >
                          {showConfirm ? <VisibilityOff sx={{ fontSize: 17 }} /> : <Visibility sx={{ fontSize: 17 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                type="submit" fullWidth variant="contained"
                disabled={loading || !token || !email || mismatch}
                sx={{
                  mt: 0.5, py: 1.25, fontWeight: 700, borderRadius: '12px',
                  bgcolor: PRIMARY,
                  boxShadow: '0 4px 16px rgba(108,99,255,0.35)',
                  '&:hover': { bgcolor: '#5850E6', boxShadow: '0 6px 20px rgba(108,99,255,0.45)' },
                  '&:disabled': { bgcolor: DIVIDER, boxShadow: 'none' },
                }}
              >
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Reset password'}
              </Button>
            </Stack>
          </Box>
        </AuthCard>
      )}
    </AuthShell>
  );
}
